import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Button } from './ui/button'
import { Icon } from './ui/icon'
import { Input } from './ui/input'

type WaitlistModalProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
	const fetcher = useFetcher()
	const [email, setEmail] = useState('')

	const isSubmitting = fetcher.state === 'submitting'
	const isSuccess = fetcher.data?.success && fetcher.state === 'idle'

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()

		const formData = new FormData()
		formData.append('email', email)
		formData.append('tier', 'free')

		void fetcher.submit(formData, {
			method: 'POST',
			action: '/api/waitlist',
		})
	}

	const handleClose = () => {
		onOpenChange(false)
		// Reset form after a delay to avoid jarring UX
		setTimeout(() => {
			setEmail('')
			if (fetcher.data) {
				fetcher.data.success = false
			}
		}, 300)
	}

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
				<Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-8 shadow-xl">
					{!isSuccess ? (
						<>
							<div className="mb-6 flex items-center justify-between">
								<Dialog.Title className="text-2xl font-bold text-gray-900">
									Join Waitlist
								</Dialog.Title>
								<Dialog.Close asChild>
									<button
										type="button"
										className="text-gray-400 hover:text-gray-600"
										aria-label="Close"
									>
										<Icon name="cross-1" className="h-6 w-6" />
									</button>
								</Dialog.Close>
							</div>

							<Dialog.Description className="mb-6 text-gray-600">
								Get notified when the app launches in April 2026.
							</Dialog.Description>

							<form onSubmit={handleSubmit} className="space-y-4">
								<HoneypotInputs />
								<div>
									<Input
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="you@example.com"
										required
										disabled={isSubmitting}
										className="w-full"
									/>
								</div>

								<Button
									type="submit"
									disabled={isSubmitting}
									className="w-full"
									size="lg"
								>
									{isSubmitting ? (
										<>
											<Icon
												name="update"
												className="mr-2 h-5 w-5 animate-spin"
											/>
											Joining...
										</>
									) : (
										'Join Waitlist'
									)}
								</Button>
							</form>
						</>
					) : (
						<div className="text-center">
							<div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
								<Icon name="check" className="h-8 w-8 text-green-600" />
							</div>

							<Dialog.Title className="mb-2 text-2xl font-bold text-gray-900">
								You're on the list!
							</Dialog.Title>

							<Dialog.Description className="mb-6 text-gray-600">
								We'll email you when the app launches in April 2026.
							</Dialog.Description>

							<Button onClick={handleClose} className="w-full" size="lg">
								Close
							</Button>
						</div>
					)}
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
